import React, { useCallback, useEffect, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import { PULSE_TYPES } from './pulseTypes'

// What the API creates for a game on first visit. The very first request creates the row but
// answers with null, so fall back to the same values rather than showing empty fields.
const DEFAULTS = { fantasy_points: 100, rs_per_inning: 50, wks_per_inning: 3 }

const threshold = (label) =>
  Yup.number().typeError('Enter a number').required(label).min(1, 'Must be at least 1')

const validationSchema = Yup.object().shape({
  fantasy_points: threshold('Fantasy Points Per Innings'),
  rs_per_inning: threshold('Runs Per Innings'),
  wks_per_inning: threshold('Wickets Per Innings'),
})

const FIELD_LABELS = {
  fantasy_points: 'Fantasy Points Per Innings',
  rs_per_inning: 'Runs Per Innings',
  wks_per_inning: 'Wickets Per Innings',
}

const TimelineSetting = ({ onLoaded, onSaved }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    CommonService.getTimelineSetting()
      .then((res) => {
        if (res.status === 200) {
          const loaded = res.data || DEFAULTS
          setSettings(loaded)
          onLoaded?.(loaded)
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [onLoaded])

  useEffect(() => {
    load()
    // Load once; onLoaded is a state setter from the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    initialValues: {
      fantasy_points: settings?.fantasy_points ?? '',
      rs_per_inning: settings?.rs_per_inning ?? '',
      wks_per_inning: settings?.wks_per_inning ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      setSaving(true)
      CommonService.setTimelineSetting(data)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            const saved = { ...settings, ...data }
            setSettings(saved)
            onSaved?.(saved)
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch((e) =>
          ToastComponent(e.response?.data?.message || 'Could not save the thresholds', 'error'),
        )
        .finally(() => setSaving(false))
    },
  })

  return (
    <CCard className="pl-panel">
      <CCardBody>
        <div className="pl-panel-heading">
          <div>
            <h2 className="pl-panel-title">Thresholds</h2>
            <p className="pl-panel-note">
              An innings makes the Pulse feed when it reaches one of these. Changes apply to the
              lists straight away.
            </p>
          </div>
        </div>

        {error ? (
          <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
            <CIcon icon={cilWarning} className="flex-shrink-0" />
            <span>We couldn&apos;t load the Pulse thresholds.</span>
            <CButton color="danger" variant="outline" size="sm" className="ms-auto" onClick={load}>
              <CIcon icon={cilReload} className="me-1" />
              Retry
            </CButton>
          </CAlert>
        ) : loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-3">
            <CSpinner size="sm" />
            <span>Loading thresholds...</span>
          </div>
        ) : (
          <CForm onSubmit={formik.handleSubmit}>
            <CRow className="g-3">
              {PULSE_TYPES.map((type) => {
                const name = type.settingKey
                const fieldError = formik.touched[name] ? formik.errors[name] : null
                return (
                  <CCol md={4} key={name}>
                    <div className="pl-threshold">
                      <div className="pl-threshold-head">
                        <span
                          className={`ge-stat-icon ge-stat-icon--${type.tone} pl-threshold-icon`}
                        >
                          <CIcon icon={type.icon} />
                        </span>
                        <CFormLabel htmlFor={name} className="pl-threshold-label">
                          {FIELD_LABELS[name]} *
                        </CFormLabel>
                      </div>
                      <CFormInput
                        type="number"
                        min={1}
                        id={name}
                        name={name}
                        value={formik.values[name]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        onWheel={(e) => e.currentTarget.blur()}
                        invalid={!!fieldError}
                      />
                      {fieldError ? (
                        <CFormFeedback invalid>{fieldError}</CFormFeedback>
                      ) : (
                        <div className="pl-threshold-help">
                          {type.label} tab: innings with {formik.values[name] || 'N'}+ {type.unit}
                        </div>
                      )}
                    </div>
                  </CCol>
                )
              })}
            </CRow>
            <div className="pl-form-actions">
              {formik.dirty && (
                <CBadge color="warning" shape="rounded-pill" className="px-3 py-2">
                  Unsaved changes
                </CBadge>
              )}
              <CLoadingButton
                type="submit"
                color="primary"
                loading={saving}
                disabled={!formik.dirty}
              >
                Save Thresholds
              </CLoadingButton>
            </div>
          </CForm>
        )}
      </CCardBody>
    </CCard>
  )
}

export default TimelineSetting
